/* PublishToGalleryModal — non-destructive "Publish to Main Gallery"
 * flow used by four admin subsystems:
 *
 *   • Event QR Photo Galleries (source_type = "event_qr")
 *   • Events editor            (source_type = "event")
 *   • Distributions editor     (source_type = "distribution")
 *   • Community Updates editor (source_type = "community_update")
 *
 * Contract with the caller:
 *   <PublishToGalleryModal
 *      open            boolean
 *      onClose         () => void
 *      sourceType      "event_qr" | "event" | "distribution" | "community_update"
 *      sourceId        string   // gallery / event / update id
 *      photos          [{ id, image_url, thumb_url?, title?, caption? }]
 *      defaultAlbumSlug? string // pre-select an album if the caller
 *                                // has a strong hint (e.g. "events")
 *      onPublished?    (result) => void
 *   />
 *
 * Behavior:
 *   • Loads active publications for (sourceType, sourceId) so we can
 *     badge photos already on the Main Gallery.
 *   • Multi-select checkboxes across all photos.
 *   • Album dropdown (from GET /api/admin/media/albums) with an
 *     inline "Create new album" flow.
 *   • Never uploads bytes — publishing only writes a reference row
 *     via POST /api/admin/gallery/publish. Sources are never modified.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { X, Loader2, CheckCircle2, Plus, ImageIcon, AlertTriangle, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_ALBUMS = `${BACKEND_URL}/api/admin/media/albums`;
const API_PUBS   = `${BACKEND_URL}/api/admin/gallery/publications`;
const API_PUB    = `${BACKEND_URL}/api/admin/gallery/publish`;
const API_UNPUB  = `${BACKEND_URL}/api/admin/gallery/publications`;  // + /{id}

const PublishToGalleryModal = ({
  open, onClose, sourceType, sourceId, photos = [],
  defaultAlbumSlug = null, onPublished,
}) => {
  const [albums, setAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState('');
  const [publications, setPublications] = useState([]);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState(new Set());
  const [showNewAlbum, setShowNewAlbum] = useState(false);
  const [newAlbumName, setNewAlbumName] = useState('');
  const [creating, setCreating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  // Unpublish flow — two-step confirmation. `confirmingUnpubId` holds
  // the publication id awaiting user confirmation; `unpublishingId`
  // holds the one currently being deleted (for spinner).
  const [confirmingUnpubId, setConfirmingUnpubId] = useState(null);
  const [unpublishingId, setUnpublishingId] = useState(null);

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true); setError(''); setSuccessMsg('');
    try {
      const [a, p] = await Promise.all([
        axios.get(API_ALBUMS, { withCredentials: true }),
        axios.get(API_PUBS, {
          withCredentials: true,
          params: { source_type: sourceType, source_id: sourceId },
        }),
      ]);
      const albumRows = a.data?.rows || [];
      setAlbums(albumRows);
      setPublications(p.data?.rows || []);
      // Preselect album: caller hint > "events" > first
      if (!selectedAlbum) {
        const bySlug = defaultAlbumSlug
          ? albumRows.find((x) => x.slug === defaultAlbumSlug)
          : null;
        const fallback = albumRows.find((x) => x.slug === 'events') || albumRows[0];
        const target = bySlug || fallback;
        if (target) setSelectedAlbum(target.id || target._id);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load albums.');
    }
    setLoading(false);
  }, [open, sourceType, sourceId, defaultAlbumSlug, selectedAlbum]);

  useEffect(() => {
    if (!open) {
      // Reset transient state whenever the modal closes so a re-open
      // starts clean (source may have changed).
      setSelectedPhotoIds(new Set());
      setSelectedAlbum('');
      setShowNewAlbum(false);
      setNewAlbumName('');
      setError(''); setSuccessMsg('');
      setConfirmingUnpubId(null);
      setUnpublishingId(null);
      return;
    }
    load();
  }, [open, load]);

  // Which of the passed-in photos are ALREADY published (to any album)?
  // Keyed by source_photo_id, value is a list of album names/ids.
  const publishedMap = useMemo(() => {
    const map = new Map();
    for (const p of publications) {
      const arr = map.get(p.source_photo_id) || [];
      const albumName = (albums.find((a) => (a.id || a._id) === p.album_id) || {}).name;
      arr.push({ pub_id: p.id, album_id: p.album_id, album_name: albumName || 'Album' });
      map.set(p.source_photo_id, arr);
    }
    return map;
  }, [publications, albums]);

  // Whether a photo is already published in the currently-selected album.
  const isInSelectedAlbum = useCallback((photoId) => {
    const arr = publishedMap.get(photoId) || [];
    return arr.some((r) => r.album_id === selectedAlbum);
  }, [publishedMap, selectedAlbum]);

  // Look up the publication id for a photo in the currently-selected album.
  const publicationIdInSelectedAlbum = useCallback((photoId) => {
    const arr = publishedMap.get(photoId) || [];
    const hit = arr.find((r) => r.album_id === selectedAlbum);
    return hit ? hit.pub_id : null;
  }, [publishedMap, selectedAlbum]);

  const handleUnpublish = async (pubId) => {
    if (!pubId) return;
    setUnpublishingId(pubId); setError(''); setSuccessMsg('');
    const successText = 'Photo unpublished from the Main Gallery. Source photo unchanged.';
    try {
      await axios.delete(`${API_UNPUB}/${pubId}`, { withCredentials: true });
      setConfirmingUnpubId(null);
      // Deselect if it was in the pending selection set.
      setSelectedPhotoIds((prev) => {
        const next = new Set(prev);
        // We don't know the source_photo_id from pubId alone here,
        // but the reload below fixes badges anyway.
        return next;
      });
      // load() clears successMsg internally, so re-set it AFTER
      // the refresh so the footer toast is visible to the admin.
      await load();
      setSuccessMsg(successText);
      if (onPublished) onPublished({ unpublished_id: pubId });
    } catch (e) {
      setError(e.response?.data?.detail || 'Unpublish failed.');
    }
    setUnpublishingId(null);
  };

  const togglePhoto = (id) => {
    if (isInSelectedAlbum(id)) return; // can't re-select an already-published photo for this album
    setSelectedPhotoIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllAvailable = () => {
    const available = photos.filter((p) => !isInSelectedAlbum(p.id)).map((p) => p.id);
    setSelectedPhotoIds(new Set(available));
  };
  const clearSelection = () => setSelectedPhotoIds(new Set());

  const createAlbum = async () => {
    const name = newAlbumName.trim();
    if (!name) return;
    setCreating(true); setError('');
    try {
      const r = await axios.post(API_ALBUMS, { name }, { withCredentials: true });
      const created = r.data;
      setAlbums((prev) => [...prev, created]);
      setSelectedAlbum(created.id || created._id);
      setShowNewAlbum(false);
      setNewAlbumName('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not create album.');
    }
    setCreating(false);
  };

  const handlePublish = async () => {
    if (!selectedAlbum) { setError('Pick an album first.'); return; }
    if (selectedPhotoIds.size === 0) {
      setError('Select at least one photo to publish.'); return;
    }
    setPublishing(true); setError(''); setSuccessMsg('');
    const items = photos
      .filter((p) => selectedPhotoIds.has(p.id))
      .map((p) => ({
        source_type: sourceType,
        source_id: sourceId,
        source_photo_id: p.id,
        image_url: p.image_url,
        thumb_url: p.thumb_url || null,
        title: p.title || null,
        caption: p.caption || null,
      }));
    try {
      const r = await axios.post(API_PUB,
        { album_id: selectedAlbum, items },
        { withCredentials: true });
      const pubCount = r.data?.total || 0;
      const skipCount = r.data?.skipped_total || 0;
      let msg = `${pubCount} photo${pubCount === 1 ? '' : 's'} published to Main Gallery.`;
      if (skipCount > 0) msg += ` ${skipCount} skipped (already published).`;
      setSuccessMsg(msg);
      setSelectedPhotoIds(new Set());
      // refresh publications so the badges update
      await load();
      if (onPublished) onPublished(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Publish failed.');
    }
    setPublishing(false);
  };

  if (!open) return null;

  const availableCount = photos.filter((p) => !isInSelectedAlbum(p.id)).length;

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
         data-testid="publish-to-gallery-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E7EB]">
          <div>
            <h3 className="font-heading font-bold text-[#0A0A0A] text-sm">
              Publish to Main Gallery
            </h3>
            <p className="text-[11px] text-[#6B7280] mt-0.5">
              Select photos and choose which Gallery album they should appear under.
              This never re-uploads or moves the source photos.
            </p>
          </div>
          <button onClick={onClose}
            className="text-[#6B7280] hover:text-[#0A0A0A]"
            data-testid="publish-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Album selector + new-album */}
        <div className="px-5 py-3 border-b border-[#E5E7EB] bg-[#F8F9FA] flex flex-wrap items-center gap-2">
          <label className="text-[11px] font-bold uppercase tracking-wide text-[#4B5563]">
            Destination album
          </label>
          <select value={selectedAlbum}
            onChange={(e) => setSelectedAlbum(e.target.value)}
            className="text-sm border border-[#E5E7EB] rounded px-2 py-1 bg-white min-w-[180px]"
            data-testid="publish-album-select">
            <option value="">— Select album —</option>
            {albums.map((a) => (
              <option key={a.id || a._id} value={a.id || a._id}>
                {a.name}
              </option>
            ))}
          </select>
          {!showNewAlbum ? (
            <button onClick={() => setShowNewAlbum(true)}
              className="text-[11px] font-semibold text-[#BA1826] hover:text-[#9E1420] inline-flex items-center gap-1"
              data-testid="publish-new-album-btn">
              <Plus className="w-3 h-3" /> New album
            </button>
          ) : (
            <div className="flex items-center gap-1.5">
              <input value={newAlbumName}
                onChange={(e) => setNewAlbumName(e.target.value)}
                placeholder="Album name"
                className="text-sm border border-[#E5E7EB] rounded px-2 py-1"
                autoFocus
                data-testid="publish-new-album-input" />
              <button onClick={createAlbum} disabled={creating || !newAlbumName.trim()}
                className="text-[11px] font-semibold px-2 py-1 rounded bg-[#0A0A0A] text-white hover:bg-black disabled:opacity-40"
                data-testid="publish-new-album-save">
                {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Create'}
              </button>
              <button onClick={() => { setShowNewAlbum(false); setNewAlbumName(''); }}
                className="text-[11px] text-[#6B7280] hover:text-[#0A0A0A]">
                Cancel
              </button>
            </div>
          )}
          <div className="ml-auto text-[11px] text-[#6B7280]">
            {photos.length} photo{photos.length === 1 ? '' : 's'} in this source
            &nbsp;·&nbsp;
            <button onClick={selectAllAvailable}
              className="underline hover:text-[#BA1826]"
              data-testid="publish-select-all-btn"
              disabled={availableCount === 0}>
              Select all available ({availableCount})
            </button>
            &nbsp;·&nbsp;
            <button onClick={clearSelection}
              className="underline hover:text-[#BA1826]"
              data-testid="publish-clear-btn">
              Clear
            </button>
          </div>
        </div>

        {/* Photo grid */}
        <div className="p-5 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" />
            </div>
          ) : photos.length === 0 ? (
            <div className="text-center py-16 text-sm text-[#6B7280]">
              <ImageIcon className="w-8 h-8 mx-auto mb-2 text-[#9CA3AF]" />
              No photos available in this source.
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3"
                 data-testid="publish-photo-grid">
              {photos.map((p) => {
                const inThisAlbum = isInSelectedAlbum(p.id);
                const publishedElsewhere = (publishedMap.get(p.id) || []).length > 0;
                const selected = selectedPhotoIds.has(p.id);
                const pubIdHere = inThisAlbum ? publicationIdInSelectedAlbum(p.id) : null;
                const isConfirming = confirmingUnpubId && confirmingUnpubId === pubIdHere;
                const isDeleting = unpublishingId && unpublishingId === pubIdHere;
                return (
                  <div
                    key={p.id}
                    className={`relative aspect-square rounded overflow-hidden border-2 transition-all text-left
                      ${inThisAlbum
                        ? 'border-green-500'
                        : selected
                          ? 'border-[#BA1826] ring-2 ring-[#BA1826]/30'
                          : 'border-transparent hover:border-[#E5E7EB]'}`}
                    data-testid={`publish-photo-${p.id}`}
                  >
                    {/* Base tile: clickable to toggle selection when not
                        already in this album; disabled inside published
                        tiles so the outer click doesn't fight the inline
                        Unpublish controls. */}
                    <button
                      type="button"
                      onClick={() => togglePhoto(p.id)}
                      disabled={inThisAlbum}
                      className={`absolute inset-0 w-full h-full ${inThisAlbum ? 'cursor-default' : 'cursor-pointer'}`}
                      aria-pressed={selected}
                      aria-label={inThisAlbum ? 'Already published to this album' : 'Toggle selection'}
                    >
                      <img src={p.thumb_url || p.image_url} alt={p.title || ''}
                        className="w-full h-full object-cover" loading="lazy" />
                    </button>

                    {/* Selection checkbox */}
                    {!inThisAlbum && (
                      <span className={`pointer-events-none absolute top-1.5 left-1.5 w-5 h-5 rounded-full flex items-center justify-center border-2
                        ${selected ? 'bg-[#BA1826] border-[#BA1826] text-white' : 'bg-white/80 border-white'}`}>
                        {selected && <CheckCircle2 className="w-3.5 h-3.5" />}
                      </span>
                    )}

                    {/* Already in THIS album — badge + Unpublish control */}
                    {inThisAlbum && !isConfirming && (
                      <div className="absolute inset-0 bg-green-600/25 flex flex-col items-center justify-center gap-1.5 pointer-events-none">
                        <span className="text-white text-[10px] font-bold uppercase inline-flex items-center gap-1 bg-green-700/85 px-1.5 py-0.5 rounded">
                          <CheckCircle2 className="w-3 h-3" /> In this album
                        </span>
                        <button
                          type="button"
                          onClick={() => setConfirmingUnpubId(pubIdHere)}
                          disabled={!pubIdHere}
                          className="pointer-events-auto text-[10px] font-semibold px-2 py-0.5 rounded bg-white text-[#BA1826] border border-[#BA1826] hover:bg-[#FEF2F2] inline-flex items-center gap-1 disabled:opacity-50"
                          data-testid={`unpublish-btn-${p.id}`}
                          aria-label="Unpublish this photo from the album">
                          <Trash2 className="w-2.5 h-2.5" /> Unpublish
                        </button>
                      </div>
                    )}

                    {/* Inline confirmation overlay */}
                    {inThisAlbum && isConfirming && (
                      <div className="absolute inset-0 bg-black/75 flex flex-col items-center justify-center gap-1.5 p-2 text-center"
                           data-testid={`unpublish-confirm-${p.id}`}>
                        <p className="text-white text-[10px] leading-tight">
                          Remove from this album?<br />
                          <span className="text-[9px] text-white/70">Source photo is not affected.</span>
                        </p>
                        <div className="flex gap-1.5">
                          <button
                            type="button"
                            onClick={() => handleUnpublish(pubIdHere)}
                            disabled={isDeleting}
                            className="text-[10px] font-semibold px-2 py-0.5 rounded bg-[#BA1826] text-white hover:bg-[#9E1420] inline-flex items-center gap-1 disabled:opacity-50"
                            data-testid={`unpublish-confirm-yes-${p.id}`}>
                            {isDeleting ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Trash2 className="w-2.5 h-2.5" />}
                            Remove
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmingUnpubId(null)}
                            disabled={isDeleting}
                            className="text-[10px] font-semibold px-2 py-0.5 rounded bg-white/20 text-white hover:bg-white/30"
                            data-testid={`unpublish-confirm-no-${p.id}`}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Published elsewhere (informational) */}
                    {!inThisAlbum && publishedElsewhere && (
                      <span className="pointer-events-none absolute bottom-1 left-1 right-1 text-[9px] bg-black/70 text-white px-1 py-0.5 rounded truncate"
                            title={publishedMap.get(p.id).map((r) => r.album_name).join(', ')}>
                        Also in: {publishedMap.get(p.id).map((r) => r.album_name).join(', ')}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-[#E5E7EB] bg-[#F8F9FA] flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1 text-[11px]">
            {error && (
              <span className="text-red-600 inline-flex items-center gap-1" data-testid="publish-error">
                <AlertTriangle className="w-3.5 h-3.5" />{error}
              </span>
            )}
            {successMsg && (
              <span className="text-green-700 inline-flex items-center gap-1" data-testid="publish-success">
                <CheckCircle2 className="w-3.5 h-3.5" />{successMsg}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose}
              className="text-xs font-semibold px-3 py-1.5 rounded border border-[#E5E7EB] text-[#4B5563] hover:bg-white"
              data-testid="publish-cancel-btn">
              Close
            </button>
            <button onClick={handlePublish}
              disabled={publishing || !selectedAlbum || selectedPhotoIds.size === 0}
              className="text-xs font-semibold px-3 py-1.5 rounded bg-[#BA1826] text-white hover:bg-[#9E1420] inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="publish-confirm-btn">
              {publishing
                ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Publishing…</>
                : <>Publish {selectedPhotoIds.size > 0 ? `(${selectedPhotoIds.size})` : ''}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublishToGalleryModal;
